
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Banknote, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { cn } from "@shared/utils/cn";
import { sanitizeHtml } from "@shared/utils/sanitize";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/components/ui/dialog";

interface ExperienceProps {
  id: number;
  title: string;
  description: string;
  image?: string;
  price?: string;
  details?: string;
  buttonLabel?: string;
  detailLabel?: string;
  onBook?: () => void;
}

// Theme-safe rich text for restaurant WYSIWYG content.
// `--foreground` is the only token whose contrast is auto-derived from card
// luminance (see useRestaurantTheme), so we anchor body color to it instead of
// `muted-foreground` (which is restaurant-controlled and can collide on dark cards).
// Tailwind's preflight strips paragraph margins, so we restore the rhythm here
// rather than via `prose` (whose hardcoded grays ignore the theme).
const RICH_TEXT_CLASS =
  "text-sm leading-relaxed text-foreground/80 [&_p]:mb-3 [&_p:last-child]:mb-0 " +
  "[&_strong]:font-semibold [&_strong]:text-foreground [&_a]:text-brand [&_a]:underline";

// Canonical price treatment — mirrors the in-flow ExperienceList row (Banknote
// icon + "<price> per person", secondary weight) so price reads consistently as
// metadata across views rather than competing with the title.
const PriceRow = ({ price, className }: { price: string; className?: string }) => {
  const { t } = useTranslation("booking");
  return (
    <div className={cn("flex items-center gap-1.5 text-sm", className)}>
      <Banknote size={16} className="flex-shrink-0" />
      <span>{price} {t("BUY_A_GIFTCARD_STRING_3")}</span>
    </div>
  );
};

const ExperienceCard = ({ experience }: { experience: ExperienceProps }) => {
  const [detailOpen, setDetailOpen] = useState(false);

  const hasDetail = !!(experience.description || experience.details);

  return (
    <>
      <Card className="overflow-hidden border-border translucent:border-surface-border hover:border-brand transition-colors group h-full flex flex-col">
        <CardContent className="p-0 flex flex-col flex-1">
          {experience.image && (
            <div className="relative">
              <div
                className="w-full h-32 bg-cover bg-center"
                style={{ backgroundImage: `url(${experience.image})` }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              {experience.price && (
                <div className="absolute bottom-0 left-0 p-3">
                  <PriceRow price={experience.price} className="text-white drop-shadow" />
                </div>
              )}
            </div>
          )}

          <div className="p-4 flex flex-col flex-1">
            <h4
              className="font-medium text-foreground"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(experience.title) }}
            />

            {experience.description && (
              <div
                className="mt-1 text-sm text-foreground/70 line-clamp-2"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(experience.description) }}
              />
            )}

            {hasDetail && (
              <button
                type="button"
                onClick={() => setDetailOpen(true)}
                className="mt-1.5 inline-flex items-center gap-0.5 self-start text-sm font-medium text-brand hover:text-brand-dark transition-colors"
              >
                {experience.detailLabel || 'View more'}
                <ChevronRight size={14} />
              </button>
            )}

            {!experience.image && experience.price && (
              <PriceRow price={experience.price} className="mt-3 text-foreground/60" />
            )}

            <div className="mt-auto pt-4">
              <Button
                variant="outline"
                size="sm"
                className="w-full bg-card border-brand text-brand hover:bg-brand-muted gap-1 group-hover:bg-brand group-hover:text-brand-foreground transition-colors"
                onClick={experience.onBook}
              >
                {experience.buttonLabel || 'Book now'}
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="flex max-h-[85vh] flex-col gap-0 overflow-hidden bg-card p-0" data-embed-inner-scroll>
          <DialogHeader className="space-y-1 border-b border-border px-6 pb-4 pt-6 pr-12 text-left">
            <DialogTitle
              className="text-xl"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(experience.title) }}
            />
            {experience.price && (
              <PriceRow price={experience.price} className="text-foreground/60" />
            )}
          </DialogHeader>

          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
            {experience.image && (
              <div
                className="w-full h-40 rounded-lg bg-cover bg-center"
                style={{ backgroundImage: `url(${experience.image})` }}
              />
            )}

            {experience.description && (
              <div
                className={RICH_TEXT_CLASS}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(experience.description) }}
              />
            )}

            {experience.details && (
              <div
                className={RICH_TEXT_CLASS}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(experience.details) }}
              />
            )}
          </div>

          <div className="border-t border-border px-6 py-4">
            <Button
              className="w-full bg-brand text-brand-foreground hover:bg-brand-dark gap-1"
              onClick={() => {
                setDetailOpen(false);
                experience.onBook?.();
              }}
            >
              {experience.buttonLabel || 'Book now'}
              <ChevronRight size={16} />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ExperienceCard;
